import React, { useEffect, useState, useCallback } from "react";
import Pagination from "react-js-pagination";
import { IoSearch } from "react-icons/io5";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import IconButton from "../../components/Button/IconButton";
import { Select, Input, TextArea, CommonSelect } from "../../components/Form";
import useFullPageLoader from "../../hooks/useFullPageLoader";
import { toastMessage } from "../../utils/toastMessage";
import findSerialNumber from "../../utils/findSerialNumber";
import Switch from "../../components/Switch/Switch";
import ModalBox from "../../components/modal/ModalBox"; // Reusing for consistency if needed, but we'll use custom modal for complex form

import {
  fetchCouponsV2Api,
  createCouponV2Api,
  updateCouponV2Api,
} from "../../services/couponsV2";
import { fetchInfluencersApi } from "../../services/influencerManagement";
import { fetchConsultantListApi } from "../../services/consultantManagement";
import CouponRedemptionLogs from "./CouponRedemptionLogs";
import "./CouponsV2.css";

const TYPE_APPLIES_ON = { INFLUENCER: "RECHARGE", CONSULTANT: "CONSULTATION", GENERAL: "CONSULTATION" };
const TYPE_LABELS = { INFLUENCER: "🌍 Influencer", CONSULTANT: "🧑‍🏫 Consultant", GENERAL: "📣 General" };
const TYPE_COLORS = { INFLUENCER: "#6c63ff", CONSULTANT: "#00b894", GENERAL: "#fd79a8" };

const formatCouponPricing = (c) => {
  if (c.appliesOn === "RECHARGE") return `${c.discountPercent}% extra coins`;
  const mins = c.duration ? `${Math.round(c.duration / 60)}m` : "wallet";
  if (c.pricingType === "FIXED") return `₹${c.fixedPrice} / ${mins}`;
  if (c.pricingType === "PERCENT") return `${c.discountPercent}% / ${mins}`;
  return `${c.discountPercent}%/min · ${mins}`;
};

const formatModes = (modes) => {
  if (!modes?.length) return "Chat + Call";
  return modes.join(", ");
};

const EMPTY_FORM = {
  code: "", type: "GENERAL", discountPercent: "", appliesOn: "CONSULTATION",
  influencerId: "", consultantId: "", consultantShare: "", validFrom: "", validTo: "", usageLimit: "", description: "",
  minAmount: "", maxAmount: "", maxDiscountAmount: "",
  pricingType: "PER_MINUTE", durationMinutes: "", fixedPrice: "",
  applicableModes: [],
  perUserLimit: "1", firstTimeUserOnly: false,
  connectButtonText: "",
};

export default function CouponsV2Management() {
  const [activeTab, setActiveTab] = useState("coupons");
  const [coupons, setCoupons] = useState([]);
  const [total, setTotal] = useState(0);
  const [activePage, setActivePage] = useState(1);
  const [loader, showLoader, hideLoader] = useFullPageLoader();
  const [filterType, setFilterType] = useState("");
  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [influencers, setInfluencers] = useState([]);
  const [consultants, setConsultants] = useState([]);
  const [loadingCons, setLoadingCons] = useState(false);

  const pageSize = 10;

  const loadCoupons = useCallback(async () => {
    showLoader();
    try {
      const params = { page: activePage - 1, pageSize };
      if (filterType) params.type = filterType;
      if (search) params.search = search;
      const res = await fetchCouponsV2Api(params);
      if (res.data.success) {
        setCoupons(res.data.data.list);
        setTotal(res.data.data.pagination.total);
      }
    } catch (e) {
      toastMessage("Failed to load coupons", "error");
    } finally {
      hideLoader();
    }
  }, [activePage, filterType, search]);

  useEffect(() => { loadCoupons(); }, [loadCoupons]);

  useEffect(() => {
    fetchInfluencersApi({ pageSize: 100 }).then(res => {
      if (res.data.success) setInfluencers(res.data.data.influencers || []);
    }).catch(() => { });
  }, []);

  const fetchConsultants = async (searchTerm = "") => {
    setLoadingCons(true);
    try {
      const res = await fetchConsultantListApi({ page: "1", limit: "500", filter: { status: true } });
      if (res.data.code === 200) {
        setConsultants(
          (res.data?.result?.list).map((c) => ({
            value: c._id,
            label: `${c.accountName || c.firstName || ""} ${c.lastName || ""}`.trim() + (c.phone ? ` (${c.phone})` : ""),
          }))
        );
      }
    } catch (e) { console.error(e); }
    setLoadingCons(false);
  };

  useEffect(() => {
    if (showModal) fetchConsultants();
  }, [showModal]);

  const handleTypeChange = (type) => {
    setForm(f => ({
      ...f,
      type,
      appliesOn: TYPE_APPLIES_ON[type],
      influencerId: "",
      consultantId: "",
      pricingType: "PER_MINUTE",
      durationMinutes: "",
      fixedPrice: "",
      applicableModes: [],
    }));
  };

  const isConsultationCoupon = form.appliesOn === "CONSULTATION";

  const toggleApplicableMode = (mode) => {
    setForm((f) => ({
      ...f,
      applicableModes: f.applicableModes.includes(mode)
        ? f.applicableModes.filter((m) => m !== mode)
        : [...f.applicableModes, mode],
    }));
  };

  const handlePageChange = (p) => setActivePage(p);

  const openCreate = () => { setForm(EMPTY_FORM); setEditId(null); setShowModal(true); };
  const openEdit = (c) => {
    setForm({
      code: c.code, type: c.type, discountPercent: c.discountPercent,
      appliesOn: c.appliesOn, influencerId: c.influencerId?._id || c.influencerId || "",
      consultantId: String(c.consultantId?._id || c.consultantId || ""),
      consultantShare: c.consultantShare ?? "",
      validFrom: c.validFrom ? new Date(c.validFrom).toISOString().slice(0, 16) : "",
      validTo: c.validTo ? new Date(c.validTo).toISOString().slice(0, 16) : "",
      usageLimit: c.usageLimit ?? "", description: c.description || "",
      minAmount: c.minAmount ?? "", maxAmount: c.maxAmount ?? "", maxDiscountAmount: c.maxDiscountAmount ?? "",
      pricingType: c.pricingType || "PER_MINUTE",
      durationMinutes: c.duration ? Math.round(c.duration / 60) : "",
      fixedPrice: c.fixedPrice ?? "",
      applicableModes: Array.isArray(c.applicableModes) ? [...c.applicableModes] : [],
      perUserLimit: c.perUserLimit ?? 1,
      firstTimeUserOnly: !!c.firstTimeUserOnly,
      connectButtonText: c.connectButtonText || "",
    });
    setEditId(c._id); setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (!form.code) {
        toastMessage("Please enter coupon code", "error");
        return;
      }
      if (form.type === "INFLUENCER" && !form.influencerId) {
        toastMessage("Please select influencer", "error");
        return;
      }
      if (form.type === "CONSULTANT" && !form.consultantId) {
        toastMessage("Please select consultant", "error");
        return;
      }
      if (form.type === "GENERAL" && !form.description) {
        toastMessage("Please enter description", "error");
        return;
      }
      if (isConsultationCoupon) {
        if (form.pricingType === "FIXED") {
          if (!form.fixedPrice || parseFloat(form.fixedPrice) <= 0) {
            toastMessage("Please enter a valid fixed price", "error");
            return;
          }
          if (!form.durationMinutes || parseInt(form.durationMinutes, 10) < 1) {
            toastMessage("Duration (minutes) is required for fixed-price coupons", "error");
            return;
          }
        } else if (!form.discountPercent || form.discountPercent < 1 || form.discountPercent > 100) {
          toastMessage("Discount percent must be between 1 and 100", "error");
          return;
        }
      } else if (!form.discountPercent || form.discountPercent < 1 || form.discountPercent > 100) {
        toastMessage("Discount percent must be between 1 and 100", "error");
        return;
      }
      if (!form.validFrom || !form.validTo) {
        toastMessage("Please select coupon validity dates", "error");
        return;
      }
      if (new Date(form.validFrom) > new Date(form.validTo)) {
        toastMessage("Valid To must be after Valid From", "error");
        return;
      }
      setSubmitting(true);
      const payload = {
        ...form,
        discountPercent: form.pricingType === "FIXED"
          ? parseFloat(form.discountPercent || 1)
          : parseFloat(form.discountPercent),
        usageLimit: form.usageLimit ? parseInt(form.usageLimit) : null,
        influencerId: form.influencerId || undefined,
        consultantId: form.consultantId || undefined,
        consultantShare: form.type === "CONSULTANT" ? parseFloat(form.consultantShare || 0) : 0,
        minAmount: form.minAmount ? parseFloat(form.minAmount) : 0,
        maxAmount: form.maxAmount ? parseFloat(form.maxAmount) : null,
        maxDiscountAmount: form.maxDiscountAmount ? parseFloat(form.maxDiscountAmount) : null,
        pricingType: isConsultationCoupon ? form.pricingType : "PER_MINUTE",
        duration: form.durationMinutes ? parseInt(form.durationMinutes, 10) * 60 : null,
        fixedPrice: form.pricingType === "FIXED" && form.fixedPrice
          ? parseFloat(form.fixedPrice)
          : null,
        applicableModes: isConsultationCoupon ? form.applicableModes : [],
        perUserLimit: form.perUserLimit ? parseInt(form.perUserLimit, 10) : 1,
        firstTimeUserOnly: !!form.firstTimeUserOnly,
        connectButtonText: (form.connectButtonText || "").trim(),
      };
      delete payload.durationMinutes;
      if (editId) await updateCouponV2Api(editId, payload);
      else await createCouponV2Api(payload);
      toastMessage(editId ? "Coupon updated!" : "Coupon created!", "success");
      setShowModal(false);
      loadCoupons();
    } catch (e) {
      toastMessage(e?.response?.data?.message || "Something went wrong.", "error");
    }
    setSubmitting(false);
  };

  const toggleStatus = async (coupon) => {
    try {
      await updateCouponV2Api(coupon._id, { isActive: !coupon.isActive });
      loadCoupons();
    } catch (e) {
      toastMessage("Failed to update status", "error");
    }
  };

  return (
    <>
      {loader}
      <div className="WrapperArea">
        <div className="WrapperBox">
          <div className="TitleBox">
            <h4 className="Title">Coupon Management V2</h4>
            {activeTab === "coupons" && (
              <div className="block_user_button">
                <button onClick={openCreate}>
                  <AddIcon />
                  Create Coupon
                </button>
              </div>
            )}
          </div>

          <div className="cv2-tabs">
            <button
              type="button"
              className={`cv2-tab ${activeTab === "coupons" ? "cv2-tab-active" : ""}`}
              onClick={() => setActiveTab("coupons")}
            >
              Coupons
            </button>
            <button
              type="button"
              className={`cv2-tab ${activeTab === "logs" ? "cv2-tab-active" : ""}`}
              onClick={() => setActiveTab("logs")}
            >
              Redemption Logs
            </button>
          </div>

          {activeTab === "logs" ? (
            <CouponRedemptionLogs />
          ) : (
          <>

          <div className="FilterBox FilterArea">
            <div className="form-group search_lg_size userSearch">
              <input
                type="search"
                className="form-control"
                placeholder="Search code or description"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <span className="user_search_icon">
                <IoSearch />
              </span>
            </div>

            <div className="group_filter_value">
              <div className="form-group select_sm_width">
                <Select
                  className="form-control"
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                >
                  <option value="">All Types</option>
                  <option value="GENERAL">General</option>
                  <option value="CONSULTANT">Consultant</option>
                  <option value="INFLUENCER">Influencer</option>
                </Select>
              </div>
            </div>
          </div>

          <div className="content-table">
            <table>
              <thead>
                <tr>
                  <th>S. No.</th>
                  <th>Code</th>
                  <th>Type</th>
                  <th>Pricing</th>
                  <th>Modes</th>
                  <th>Valid Until</th>
                  <th>Usage</th>
                  <th>Limits</th>
                  <th>Status</th>
                  <th className="text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {coupons.length > 0 ? (
                  coupons.map((c, index) => (
                    <tr key={c._id}>
                      <td>{findSerialNumber(index, activePage, pageSize)}</td>
                      <td>
                        <strong style={{ color: "#CD2F38" }}>{c.code}</strong>
                        {c.description && <div style={{ fontSize: "11px", color: "#666" }}>{c.description}</div>}
                      </td>
                      <td>
                        <span className="badge" style={{ background: TYPE_COLORS[c.type], color: "#fff", padding: "4px 8px", borderRadius: "4px" }}>
                          {TYPE_LABELS[c.type]}
                        </span>
                        {c.type === "CONSULTANT" && <div style={{ fontSize: "10px", marginTop: "4px" }}>Share: {c.consultantShare}%</div>}
                      </td>
                      <td>{formatCouponPricing(c)}</td>
                      <td style={{ fontSize: "11px" }}>{formatModes(c.applicableModes)}</td>
                      <td>{c.validTo ? new Date(c.validTo).toLocaleString("en-IN", { hour12: true, timeZone: 'Asia/Kolkata' }) : "—"}</td>
                      <td>{c.usageCount} / {c.usageLimit || "∞"} · user: {c.perUserLimit ?? 1}</td>
                      <td>
                        <div style={{ fontSize: "11px" }}>
                          {c.minAmount > 0 && <div>Min: ₹{c.minAmount}</div>}
                          {c.maxDiscountAmount > 0 && <div>Cap: ₹{c.maxDiscountAmount}</div>}
                          {!c.minAmount && !c.maxDiscountAmount && <span style={{ color: "#999" }}>No Limits</span>}
                        </div>
                      </td>
                      <td>
                        <Switch
                          checked={c.isActive}
                          onChange={() => toggleStatus(c)}
                        />
                      </td>
                      <td className="text-center">
                        <IconButton onClick={() => openEdit(c)}>
                          <EditIcon className="edit_icon" />
                        </IconButton>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan={10} className="text-center py-4">No coupons found.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {total > pageSize && (
            <div className="pagination-wrapper">
              <div className="Pagination">
                <Pagination
                  activePage={activePage}
                  itemsCountPerPage={pageSize}
                  totalItemsCount={total}
                  pageRangeDisplayed={5}
                  onChange={handlePageChange}
                  hideDisabled={true}
                />
              </div>
            </div>
          )}
          </>
          )}
        </div>
      </div>

      <ModalBox
        status={showModal}
        onClose={() => setShowModal(false)}
        title={editId ? "Edit Coupon" : "Create Coupon"}
        closeIcon
        size="md"
        noFooter
      >
        <form onSubmit={handleSubmit}>
          <div className="grid-responsive">
            <div className="form-group">
              <Input
                label="Coupon Code"
                required
                className="form-control"
                value={form.code}
                disabled={!!editId}
                onChange={(e) =>
                  setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))
                }
                placeholder="e.g. ASTRO10"
              />
            </div>
            <div className="form-group">
              <Select
                label="Coupon Type"
                required
                className="form-control"
                value={form.type}
                disabled={!!editId}
                onChange={(e) => handleTypeChange(e.target.value)}
              >
                <option value="GENERAL">📣 General (Consultation)</option>
                <option value="CONSULTANT">🧑‍🏫 Consultant (Consultation)</option>
                <option value="INFLUENCER">🌍 Influencer (Recharge)</option>
              </Select>
            </div>

            {form.type === "INFLUENCER" && (
              <>
                <div className="form-group">
                  <Select
                    label="Applies On"
                    required
                    className="form-control"
                    value={form.appliesOn}
                    disabled={!!editId}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        appliesOn: e.target.value,
                        pricingType: "PER_MINUTE",
                        durationMinutes: "",
                        fixedPrice: "",
                        applicableModes: [],
                      }))
                    }
                  >
                    <option value="RECHARGE">Recharge (extra coins)</option>
                    <option value="CONSULTATION">Consultation (discount)</option>
                  </Select>
                </div>
                <div className="form-group" style={{ gridColumn: "1 / -1" }}>
                  <Select
                    label="Select Influencer"
                    required
                    className="form-control"
                    value={form.influencerId}
                    disabled={!!editId}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, influencerId: e.target.value }))
                    }
                  >
                    <option value="">— Select Influencer —</option>
                    {influencers.map((inf) => (
                      <option key={inf._id} value={inf._id}>
                        {inf.name} ({inf.email})
                      </option>
                    ))}
                  </Select>
                </div>
              </>
            )}

            {form.type === "CONSULTANT" && (
              <>
                <div className="form-group" style={{ gridColumn: "1 / -1" }}>
                  <CommonSelect
                    label="Select Consultant"
                    required
                    isSearchable
                    className="form-control-select"
                    options={consultants}
                    loading={loadingCons}
                    onChange={(val) =>
                      setForm((f) => ({
                        ...f,
                        consultantId: val != null && val !== '' ? String(val) : '',
                      }))
                    }
                    value={
                      consultants.find(
                        (c) => String(c.value) === String(form.consultantId),
                      ) || null
                    }
                  />
                </div>
                <div className="form-group">
                  <Input
                    type="number"
                    label="Consultant Share %"
                    required
                    className="form-control"
                    value={form.consultantShare}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, consultantShare: e.target.value }))
                    }
                    placeholder="e.g. 10"
                  />
                </div>
              </>
            )}

            {isConsultationCoupon && (
              <>
                <div className="form-group" style={{ gridColumn: "1 / -1", borderTop: "1px solid #eee", paddingTop: "10px", marginTop: "6px" }}>
                  <h6 style={{ fontSize: "14px", fontWeight: "bold", marginBottom: "10px" }}>Consultation Pricing</h6>
                </div>

                <div className="form-group">
                  <Select
                    label="Pricing Type"
                    required
                    className="form-control"
                    value={form.pricingType}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        pricingType: e.target.value,
                        fixedPrice: "",
                        durationMinutes: "",
                      }))
                    }
                  >
                    <option value="PER_MINUTE">Per Minute (% off rate)</option>
                    <option value="FIXED">Fixed Price (flat for duration)</option>
                    <option value="PERCENT">Percent (discounted rate × minutes)</option>
                  </Select>
                </div>

                {form.pricingType === "FIXED" ? (
                  <div className="form-group">
                    <Input
                      type="number"
                      label="Fixed Price (coins)"
                      required
                      className="form-control"
                      value={form.fixedPrice}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, fixedPrice: e.target.value }))
                      }
                      placeholder="e.g. 99"
                    />
                  </div>
                ) : (
                  <div className="form-group">
                    <Input
                      type="number"
                      label="Discount %"
                      required
                      className="form-control"
                      value={form.discountPercent}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, discountPercent: e.target.value }))
                      }
                      placeholder="e.g. 10"
                    />
                  </div>
                )}

                <div className="form-group">
                  <Input
                    type="number"
                    label={form.pricingType === "PER_MINUTE" ? "Duration (minutes, optional)" : "Duration (minutes)"}
                    required={form.pricingType === "FIXED"}
                    optional={form.pricingType === "PER_MINUTE"}
                    className="form-control"
                    value={form.durationMinutes}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, durationMinutes: e.target.value }))
                    }
                    placeholder={form.pricingType === "PER_MINUTE" ? "Blank = wallet-limited" : "e.g. 10"}
                  />
                </div>

                <div className="form-group" style={{ gridColumn: "1 / -1" }}>
                  <label className="my-1 text-dark">Applicable Modes</label>
                  <div style={{ display: "flex", gap: "16px", marginTop: "6px" }}>
                    <label style={{ display: "flex", alignItems: "center", gap: "6px", cursor: "pointer" }}>
                      <input
                        type="checkbox"
                        checked={form.applicableModes.includes("CHAT")}
                        onChange={() => toggleApplicableMode("CHAT")}
                      />
                      Chat
                    </label>
                    <label style={{ display: "flex", alignItems: "center", gap: "6px", cursor: "pointer" }}>
                      <input
                        type="checkbox"
                        checked={form.applicableModes.includes("CALL")}
                        onChange={() => toggleApplicableMode("CALL")}
                      />
                      Call / Audio
                    </label>
                  </div>
                  <small style={{ color: "#888" }}>Leave both unchecked = available on Chat and Call</small>
                </div>
              </>
            )}

            {!isConsultationCoupon && (
              <div className="form-group">
                <Input
                  type="number"
                  label="Extra Coins %"
                  required
                  className="form-control"
                  value={form.discountPercent}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, discountPercent: e.target.value }))
                  }
                  placeholder="e.g. 10"
                />
              </div>
            )}

            <div className="form-group">
              <Input
                type="number"
                label="Total Usage Limit (all users)"
                optional
                className="form-control"
                value={form.usageLimit}
                onChange={(e) =>
                  setForm((f) => ({ ...f, usageLimit: e.target.value }))
                }
                placeholder="Leave blank for unlimited"
              />
            </div>

            <div className="form-group">
              <Input
                type="number"
                label="Per-User Limit"
                className="form-control"
                value={form.perUserLimit}
                onChange={(e) =>
                  setForm((f) => ({ ...f, perUserLimit: e.target.value }))
                }
                placeholder="Default: 1"
              />
            </div>

            <div className="form-group" style={{ gridColumn: "1 / -1" }}>
              <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={form.firstTimeUserOnly}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, firstTimeUserOnly: e.target.checked }))
                  }
                />
                First-time users only (blocks same device/phone reuse)
              </label>
            </div>

            <div className="form-group">
              <Input
                type="datetime-local"
                label="Valid From"
                className="form-control"
                value={form.validFrom}
                onChange={(e) =>
                  setForm((f) => ({ ...f, validFrom: e.target.value }))
                }
              />
            </div>
            <div className="form-group">
              <Input
                type="datetime-local"
                label="Valid To"
                className="form-control"
                value={form.validTo}
                onChange={(e) =>
                  setForm((f) => ({ ...f, validTo: e.target.value }))
                }
              />
            </div>

            <div className="form-group" style={{ gridColumn: "1 / -1" }}>
              <TextArea
                label="Description"
                className="form-control"
                value={form.description}
                onChange={(e) =>
                  setForm((f) => ({ ...f, description: e.target.value }))
                }
                placeholder="Optional description..."
                rows={2}
              />
            </div>

            {isConsultationCoupon && (
              <div className="form-group" style={{ gridColumn: "1 / -1" }}>
                <Input
                  type="text"
                  label="Connect Button Text (optional)"
                  className="form-control"
                  value={form.connectButtonText}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, connectButtonText: e.target.value }))
                  }
                  placeholder="e.g. Connect Now @₹99 — leave blank for auto label"
                  maxLength={80}
                />
              </div>
            )}

            <div className="form-group" style={{ gridColumn: "1 / -1", borderTop: "1px solid #eee", paddingTop: "10px", marginTop: "10px" }}>
              <h6 style={{ fontSize: "14px", fontWeight: "bold", marginBottom: "10px" }}>Coupon Constraints & Limits</h6>
            </div>

            <div className="form-group">
              <Input
                type="number"
                label="Min Base Amount"
                className="form-control"
                value={form.minAmount}
                onChange={(e) => setForm((f) => ({ ...f, minAmount: e.target.value }))}
                placeholder="0"
              />
            </div>

            <div className="form-group">
              <Input
                type="number"
                label="Max Discount Cap"
                className="form-control"
                value={form.maxDiscountAmount}
                onChange={(e) => setForm((f) => ({ ...f, maxDiscountAmount: e.target.value }))}
                placeholder="None"
              />
            </div>

            <div className="form-group">
              <Input
                type="number"
                label="Max Base Amount"
                className="form-control"
                value={form.maxAmount}
                onChange={(e) => setForm((f) => ({ ...f, maxAmount: e.target.value }))}
                placeholder="None"
              />
            </div>

            <div className="form-group" style={{ gridColumn: "1 / -1", marginTop: "1rem" }}>
              <button
                type="submit"
                className="btn btn-primary w-100"
                disabled={submitting}
              >
                {submitting
                  ? "Saving..."
                  : editId
                    ? "Update Coupon"
                    : "Create Coupon"}
              </button>
            </div>
          </div>
        </form>
      </ModalBox>
    </>
  );
}
